import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  File,
  FileText,
  FileType,
  History,
  Loader2,
  MessageSquareText,
  Pencil,
  PencilOff,
  RotateCcw,
} from "lucide-react";
import { Button, ListBox, Select } from "react-aria-components";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";
import { KitSelectItem } from "@/app/routes/dashboard/components/kit/Select";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { getAiRequestErrorMessage } from "@/lib/api";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import { queue } from "@/components/Toast";
import { useAuthStore } from "@/stores/authStore";
import type { WorkshopArtifactRevision } from "@/types/workshop.type";
import ArtifactConflictNotice from "./ArtifactConflictNotice";
import ArtifactDocument from "./ArtifactDocument";
import { OlderVersionStrip, SavedLine, StashStrip } from "./ArtifactStrips";
import RichDocEditor from "./RichDocEditor";
import {
  useWorkshopArtifact,
  useWorkshopArtifactMutations,
  useWorkshopArtifactRevisions,
  readArtifactConflict,
  type ArtifactEditConflict,
} from "../api/useWorkshopArtifacts";
import {
  useWorkshopArtifactNoteMutations,
  useWorkshopArtifactNotes,
} from "../api/useWorkshopArtifactNotes";
import { useWorkshopProject } from "../api/useWorkshopProjects";
import { AddNoteButton, ArtifactNotesDrawer, useArtifactNoteLayer } from "./ArtifactNotes";
import type { NoteAnchor } from "./artifactNoteAnchors";
import { forgetDraft, readDraft, stashDraft, type StashedDraft } from "./artifactDraftStash";
import { useWorkshopUndo } from "./WorkshopUndo";
import { formatDayTime, savedByOf } from "./docAttribution";
import {
  formatCount,
  parseDocument,
  repeatedTitle,
  replaceRange,
  retitledLine,
} from "./documentBlocks";
import {
  exportArtifactDocx,
  exportArtifactMarkdown,
  exportArtifactPdf,
} from "./exportWorkshopArtifact";
import { documentTag } from "../workshopOutputs";
import { EXPORT_FORMATS } from "./WorkshopOutputMenu";
import { KindTag, StateTag } from "./WorkshopParts";

/** The title rule: "Type, subject", two lines, this many characters at most. */
const TITLE_LIMIT = 72;

const EXPORT_ICONS = { md: FileText, docx: FileType, pdf: File } as const;

/** Revision notes the API writes itself (creative-os-backend WorkshopArtifactUseCases). */
const MACHINE_NOTES = new Set(["created", "manual edit"]);

/** Line endings as a textarea gives them back: an edited block is LF on purpose. */
function lf(text: string): string {
  return text.replace(/\r\n?/g, "\n");
}

/** "v2 · you · Sep 24, 14:05 · Tightened the targets" for the version picker. */
/** One "key: value" line of a document's front matter. */
type FrontFact = { key: string; value: string };

type EditorSeed = { key: number; front: string; h1: string; body: string; fresh: boolean };

const FRONT_MATTER = /^---[ \t]*\r?\n([\s\S]*?)\r?\n(?:---|\.\.\.)[ \t]*(?:\r?\n|$)/;
const FRONT_LINE = /^([A-Za-z][\w -]*):[ \t]*(.*)$/;

/** The parts of a document the rich editor leaves out, and the text it edits. */
function splitForEditor(
  content: string,
  title: string,
): { facts: FrontFact[] | null; front: string; h1: string; body: string } {
  let rest = content;
  let front = "";
  let facts: FrontFact[] | null = null;
  const matter = FRONT_MATTER.exec(content);
  if (matter) {
    rest = content.slice(matter[0].length).replace(/^\s+/, "");
    const lines = matter[1].split(/\r?\n/).filter((line) => line.trim());
    const parsed = lines.map((line) => FRONT_LINE.exec(line));
    if (parsed.length > 0 && parsed.every((m) => m !== null)) {
      facts = parsed.map((m) => ({ key: m![1], value: m![2] }));
    } else {
      front = `${matter[0].trimEnd()}\n\n`;
    }
  }
  const repeated = repeatedTitle(parseDocument(rest), title);
  const h1 = repeated ? `${rest.slice(repeated.start, repeated.end).trimEnd()}\n\n` : "";
  const body = repeated ? replaceRange(rest, repeated.start, repeated.end, "").replace(/^\s+/, "") : rest;
  return { facts, front, h1, body };
}

function frontMatterOf(facts: FrontFact[]): string {
  return `---\n${facts.map((fact) => `${fact.key}: ${fact.value.replace(/\s*\n\s*/g, " ")}`).join("\n")}\n---\n\n`;
}

function revisionLabel(revision: WorkshopArtifactRevision, viewerId: string | null): string {
  // Older cache entries may predate attribution; fall back to the author kind.
  const author =
    savedByOf(revision.attribution?.createdBy, viewerId)?.who ??
    (revision.authorType === "agent"
      ? "agent"
      : revision.authorType === "user"
        ? "a person"
        : "Workshop");
  // The API's own notes ("created", "manual edit") are not the person's words.
  const note = revision.note?.trim() ?? "";
  const shown = note && !MACHINE_NOTES.has(note.toLowerCase()) ? ` · ${note}` : "";
  return `v${revision.version} · ${author} · ${formatDayTime(revision.createdAt)}${shown}`;
}

function splitFirstImage(content: string): {
  before: string;
  after: string;
  image: { src: string; alt: string } | null;
} {
  const match = /!\[([^\]]*)\]\(([^)\s]+)\)/m.exec(content);
  if (!match) return { before: content, after: "", image: null };

  const src = match[2];
  if (!/^(?:https?:\/\/|\/)/i.test(src)) {
    return { before: content, after: "", image: null };
  }

  return {
    before: content.slice(0, match.index).trimEnd(),
    after: content.slice(match.index + match[0].length).trimStart(),
    image: { src, alt: match[1] || "Artifact image" },
  };
}

type EditorialSection = {
  heading: string;
  content: string;
};

type EditorialArtifact = {
  title: string;
  image: { src: string; alt: string };
  lead: string | null;
  caption: string | null;
  columns: EditorialSection[];
  opportunity: EditorialSection | null;
  supplementalSections: EditorialSection[];
};

function normalizedHeading(heading: string): string {
  return heading
    .replace(/[\\*_`~]/g, "")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toLowerCase();
}

function isClosingSection(heading: string): boolean {
  return /^(?:opportunity|recommendations?|next steps?|closing thought|conclusion)$/.test(
    normalizedHeading(heading),
  );
}

function editorialPreamble(content: string): string | null {
  const trimmed = content.trim();
  if (!trimmed) return null;

  const asterisk = /^\*([\s\S]+)\*$/.exec(trimmed);
  if (asterisk?.[1]?.trim()) return asterisk[1].trim();

  const underscore = /^_([\s\S]+)_$/.exec(trimmed);
  if (underscore?.[1]?.trim()) return underscore[1].trim();

  return trimmed;
}

/**
 * Promote image-led, sectioned markdown into the editorial reading layout.
 * Headings stay content-driven: any two or more H2 sections qualify, while a
 * conventional closing section is held for the ruled footer. Simpler documents
 * keep the general markdown renderer.
 */
function parseEditorialArtifact(content: string): EditorialArtifact | null {
  const normalizedContent = content.replace(/\r\n?/g, "\n");
  const leadingContent = normalizedContent.trimStart();
  const leadingOffset = normalizedContent.length - leadingContent.length;
  const h1Match =
    /^#(?!#)[ \t]+(.+?)(?:[ \t]+#+)?[ \t]*(?:\n|$)/.exec(leadingContent);
  const title = h1Match?.[1]?.trim();
  if (!h1Match || !title) return null;

  const h1End = leadingOffset + h1Match[0].length;
  const sectionPattern = /^##(?!#)[ \t]+(.+?)(?:[ \t]+#+)?[ \t]*$/gm;
  const sectionMatches = Array.from(normalizedContent.matchAll(sectionPattern)).filter(
    (match) => (match.index ?? -1) >= h1End,
  );
  const firstSectionIndex = sectionMatches[0]?.index;
  if (firstSectionIndex === undefined) return null;

  const preamble = normalizedContent.slice(h1End, firstSectionIndex);
  const preambleParts = splitFirstImage(preamble);
  if (!preambleParts.image) return null;

  const sections = sectionMatches.map((match, index) => ({
    heading: match[1].trim(),
    content: normalizedContent
      .slice(
        (match.index ?? 0) + match[0].length,
        sectionMatches[index + 1]?.index ?? normalizedContent.length,
      )
      .trim(),
  })).filter((section) => section.heading && section.content);
  if (sections.length < 2) return null;

  const closingIndex = sections.findIndex((section) =>
    isClosingSection(section.heading),
  );
  const opportunity = closingIndex >= 0 ? sections[closingIndex] : null;
  const bodySections = sections.filter((_, index) => index !== closingIndex);
  if (bodySections.length < 2) return null;

  const columns = bodySections.slice(0, 3);
  const supplementalSections = bodySections.slice(3);

  return {
    title,
    image: preambleParts.image,
    lead: editorialPreamble(preambleParts.before),
    caption: editorialPreamble(preambleParts.after),
    columns,
    opportunity,
    supplementalSections,
  };
}

type Props = {
  projectId: string;
  artifactId: string;
  canWrite: boolean;
  /** A phone: the controls sit under the title, menus and notes are sheets. */
  isPhone?: boolean;
  /** The breadcrumb's Outputs: back to the room's list. */
  onBack: () => void;
  /** The pane's own controls (Full width, Close), at the end of the head. */
  actions: ReactNode;
  /** Rewrite: put a block's text in the thread's composer as a quote. Never sends it. */
  onQuoteInThread?: (text: string) => void;
  /** The thread a version was saved in, when it is the viewer's own. */
  onOpenThread?: (threadId: string) => void;
  /** Editing started or ended, and whether anything is unsaved (the pane will not close under it). */
  onEditingChange?: (state: { editing: boolean; dirty: boolean }) => void;
};

/**
 * A document in the split desk's pane (stage 3). It reads as a document (one
 * title, the facts strip, definition rows, tables, numbered sections with an
 * index). Edit opens the whole document as rich text (RichDocEditor: no raw
 * markdown, the format bar and Rewrite over the passage with the caret), and a
 * save stores the text as the next version. Versions are not compared: there
 * is no before and after (user call, 12 Oct). An older version reads read-only
 * under a strip; restoring and saving over someone who saved first both make a
 * new version.
 */
export default function ArtifactLens({
  projectId,
  artifactId,
  canWrite,
  isPhone = false,
  onBack,
  actions,
  onQuoteInThread,
  onOpenThread,
  onEditingChange,
}: Props) {
  const artifactQuery = useWorkshopArtifact(artifactId);
  const revisionsQuery = useWorkshopArtifactRevisions(artifactId);
  const { updateArtifact } = useWorkshopArtifactMutations(projectId);
  const titleId = useId();
  const noteId = useId();
  const headingId = useId();
  const counterId = useId();
  const actionsRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef = useRef<HTMLButtonElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const restoreEditFocusRef = useRef(false);
  const viewerId = useAuthStore((state) => state.user?.id ?? null);

  const [editing, setEditing] = useState(false);
  const [viewRevisionId, setViewRevisionId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [draftNote, setDraftNote] = useState("");
  // What the edit started from, to tell what is unsaved: the text as the editor
  // first wrote it back (base), and as it was stored (original).
  const [baseTitle, setBaseTitle] = useState("");
  const [baseContent, setBaseContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  // What the editor opens with. A new key starts it over (a new edit, Undo,
  // someone else's version loaded); `fresh` makes its first write-back the base.
  const [seed, setSeed] = useState<EditorSeed>({ key: 0, front: "", h1: "", body: "", fresh: true });
  // The front matter's values (the facts strip), edited as fields over the text.
  const [facts, setFacts] = useState<FrontFact[] | null>(null);
  const [editorBody, setEditorBody] = useState("");
  // The version this edit started from, and whether someone saved over it since.
  const [editBase, setEditBase] = useState<number | null>(null);
  const [conflict, setConflict] = useState<ArtifactEditConflict | null>(null);
  // A restore in flight: the version being brought back.
  const [restoring, setRestoring] = useState<number | null>(null);
  // An unsaved edit this document was left with (another project, a reload): offered back.
  const [stashed, setStashed] = useState<StashedDraft | null>(() => readDraft(viewerId, artifactId));
  const undo = useWorkshopUndo();
  // The Undo toast this document put up, taken down if the document goes.
  const undoKeyRef = useRef<number | null>(null);
  // Notes pinned to passages: the drawer, the passage being noted, the note in focus.
  const [notesOpen, setNotesOpen] = useState(false);
  const [draftAnchor, setDraftAnchor] = useState<NoteAnchor | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<HTMLDivElement>(null);
  const notesQuery = useWorkshopArtifactNotes(projectId, artifactId);
  const { createNote, updateNote, deleteNote } = useWorkshopArtifactNoteMutations(
    projectId,
    artifactId,
  );
  const isProjectOwner = useWorkshopProject(projectId).data?.project.isOwner ?? false;

  const artifact = artifactQuery.data ?? null;
  const revisions = useMemo(
    () => [...(revisionsQuery.data ?? [])].sort((a, b) => b.version - a.version),
    [revisionsQuery.data],
  );
  const currentVersion = artifact?.version ?? 1;
  // The API lists every version, the current one included.
  const olderRevisions = revisions.filter((revision) => revision.version < currentVersion);
  const currentRevision = revisions.find((revision) => revision.version === currentVersion) ?? null;
  const viewedRevision = viewRevisionId
    ? (olderRevisions.find((revision) => revision.id === viewRevisionId) ?? null)
    : null;
  const displayedVersion = viewedRevision?.version ?? currentVersion;
  const displayedContent = viewedRevision?.content ?? artifact?.content ?? "";
  const canEdit = canWrite && !!artifact && artifact.status !== "archived" && !viewedRevision;

  const readDoc = useMemo(() => parseDocument(displayedContent), [displayedContent]);
  const editDoc = useMemo(() => parseDocument(draftContent), [draftContent]);
  const effectiveContent = draftContent;
  // Line endings alone are not a change.
  const dirty =
    editing && (lf(effectiveContent) !== lf(baseContent) || draftTitle.trim() !== baseTitle.trim());

  // The document moved on (a poll saw a new version) before its history did:
  // read the history again, or the new version has no author to review.
  const newestListed = revisions[0]?.version ?? 0;
  const { refetch: refetchRevisions } = revisionsQuery;
  const revisionsLoaded = !!revisionsQuery.data;
  useEffect(() => {
    if (revisionsLoaded && currentVersion > newestListed) void refetchRevisions();
  }, [currentVersion, newestListed, revisionsLoaded, refetchRevisions]);

  useEffect(() => {
    onEditingChange?.({ editing, dirty });
  }, [onEditingChange, editing, dirty]);
  // Leaving the pane ends the edit as far as the pane is concerned.
  useEffect(() => () => onEditingChange?.({ editing: false, dirty: false }), [onEditingChange]);

  // Unsaved changes: the browser asks before the page goes, and the edit is
  // kept in this tab for when the document opens again (another project, a
  // brand switch, leaving Workshop).
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older Chrome and Safari only ask when returnValue is set as well.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
  useEffect(() => {
    if (!editing || editBase === null) return;
    if (!dirty) {
      forgetDraft(viewerId, artifactId);
      return;
    }
    stashDraft(viewerId, {
      artifactId,
      baseVersion: editBase,
      title: draftTitle,
      content: effectiveContent,
      note: draftNote,
    });
  }, [artifactId, viewerId, editing, dirty, editBase, draftTitle, effectiveContent, draftNote]);
  // A toast this document offered has nothing to undo once it is gone.
  useEffect(
    () => () => {
      if (undoKeyRef.current !== null) undo.dismiss(undoKeyRef.current);
    },
    // Dismiss only on unmount; the undo API changes as toasts come and go.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Stable while the query is: the note layer re-anchors whenever this changes.
  const notes = useMemo(() => notesQuery.data ?? [], [notesQuery.data]);
  const openNoteCount = notes.filter((note) => note.status === "open").length;
  // Passages are found only in the current version, and only while it is on screen.
  const notesLive = !!artifact && !editing && !viewedRevision;
  const pickNote = useCallback((noteId: string) => {
    setActiveNoteId(noteId);
    setNotesOpen(true);
  }, []);
  const noteLayer = useArtifactNoteLayer({
    docRef,
    scrollRef,
    notes,
    enabled: notesLive,
    canAdd: canEdit,
    draft: draftAnchor,
    activeNoteId,
    onPickNote: pickNote,
  });

  const startNote = (anchor: NoteAnchor) => {
    window.getSelection()?.removeAllRanges();
    noteLayer.clearPrompt();
    setActiveNoteId(null);
    setDraftAnchor(anchor);
    setNotesOpen(true);
  };

  const submitNote = (body: string) => {
    if (!artifact || !draftAnchor) return;
    createNote.mutate({
      tempId: `temp-${crypto.randomUUID()}`,
      ...draftAnchor,
      body,
      artifactVersion: artifact.version,
    });
    setDraftAnchor(null);
  };

  const closeNotes = () => {
    setNotesOpen(false);
    setDraftAnchor(null);
    setActiveNoteId(null);
  };

  useEffect(() => {
    const focusFrame = window.requestAnimationFrame(() => {
      breadcrumbRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(focusFrame);
  }, [artifactId, artifactQuery.isLoading]);

  useEffect(() => {
    if (!menuOpen) return;
    const focusFrame = window.requestAnimationFrame(() => {
      menuRef.current
        ?.querySelector<HTMLElement>('[role="menuitem"]')
        ?.focus();
    });
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (actionsRef.current?.contains(target) || menuButtonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (editing || !restoreEditFocusRef.current) return;
    restoreEditFocusRef.current = false;
    const focusFrame = window.requestAnimationFrame(() => {
      (editButtonRef.current ?? menuButtonRef.current)?.focus();
    });
    return () => window.cancelAnimationFrame(focusFrame);
  }, [editing]);

  const resetDraft = () => {
    setDraftTitle("");
    setDraftContent("");
    setDraftNote("");
    setBaseTitle("");
    setBaseContent("");
    setOriginalContent("");
  };

  /**
   * Opens the editor on this text. Two parts stay out of the rich text and are
   * put back in front of it: the front matter (its values become fields; front
   * matter that is more than "key: value" lines is kept as it is) and the H1
   * that repeats the title (the title field edits it).
   */
  const seedEditor = (content: string, title: string, fresh: boolean) => {
    const split = splitForEditor(content, title);
    setFacts(split.facts);
    setEditorBody(split.body);
    setSeed((current) => ({ key: current.key + 1, front: split.front, h1: split.h1, body: split.body, fresh }));
  };

  const compose = (nextFacts: FrontFact[] | null, body: string) =>
    (nextFacts ? frontMatterOf(nextFacts) : seed.front) + seed.h1 + body;

  /** The editor wrote the document back: the draft, and on a fresh open the base too. */
  const onEditorChange = (body: string, opened: boolean) => {
    const full = compose(facts, body);
    setEditorBody(body);
    setDraftContent(full);
    if (opened && seed.fresh) setBaseContent(full);
  };

  const onFactChange = (index: number, value: string) => {
    if (!facts) return;
    const next = facts.map((fact, i) => (i === index ? { ...fact, value } : fact));
    setFacts(next);
    setDraftContent(compose(next, editorBody));
  };

  /** A new edit retires any Undo still on offer, so it can never overwrite what comes next. */
  const retireUndo = () => {
    if (undoKeyRef.current === null) return;
    undo.dismiss(undoKeyRef.current);
    undoKeyRef.current = null;
  };

  const startEditing = () => {
    if (!artifact) return;
    retireUndo();
    setMenuOpen(false);
    closeNotes();
    setDraftTitle(artifact.title);
    setDraftContent(artifact.content);
    setBaseTitle(artifact.title);
    setBaseContent(artifact.content);
    setOriginalContent(artifact.content);
    seedEditor(artifact.content, artifact.title, true);
    setDraftNote("");
    setEditBase(artifact.version);
    setConflict(null);
    setEditing(true);
  };

  /** Everything an edit holds, to put back exactly (Undo). */
  type DraftSnapshot = {
    title: string;
    content: string;
    note: string;
    baseTitle: string;
    baseContent: string;
    originalContent: string;
    editBase: number | null;
    conflict: ArtifactEditConflict | null;
  };
  const snapshot = (): DraftSnapshot => ({
    title: draftTitle,
    content: draftContent,
    note: draftNote,
    baseTitle,
    baseContent,
    originalContent,
    editBase,
    conflict,
  });
  const putBack = (draft: DraftSnapshot) => {
    setMenuOpen(false);
    closeNotes();
    setViewRevisionId(null);
    setDraftTitle(draft.title);
    setDraftContent(draft.content);
    setDraftNote(draft.note);
    setBaseTitle(draft.baseTitle);
    setBaseContent(draft.baseContent);
    setOriginalContent(draft.originalContent);
    seedEditor(draft.content, draft.baseTitle, false);
    setEditBase(draft.editBase);
    setConflict(draft.conflict);
    setEditing(true);
  };
  /** A five second Undo in Workshop's toast; taken down if this document goes first. */
  const offerUndo = (title: string, line: string, icon: typeof PencilOff, onUndo: () => void) => {
    undoKeyRef.current = undo.show({ icon, title, line, undo: onUndo });
  };

  // Confirm if permanent, undo if not: unsaved changes go at once, with Undo.
  const cancelEditing = () => {
    const draft = dirty ? snapshot() : null;
    restoreEditFocusRef.current = true;
    setConflict(null);
    setEditing(false);
    resetDraft();
    forgetDraft(viewerId, artifactId);
    if (draft) {
      offerUndo(
        "Your changes were not saved",
        "Undo puts them back where you left them, still unsaved.",
        PencilOff,
        () => putBack(draft),
      );
    }
  };

  // Rewrite: the passage's words go to the thread's composer as they are, with
  // nothing added; the person says what they want there.
  const rewrite = onQuoteInThread
    ? (text: string) => {
        if (text) onQuoteInThread(text);
      }
    : undefined;

  /** `baseVersion` overrides where this edit started (saving over a newer version). */
  const saveEdit = (baseVersion?: number) => {
    if (!artifact || !effectiveContent.trim()) return;
    const title = draftTitle.trim();
    // Only the title changed: the stored text goes back as it was, not as the
    // editor rewrote it.
    let content = lf(effectiveContent) === lf(baseContent) && originalContent ? originalContent : effectiveContent;
    // The document's own H1 repeated the title: it keeps repeating it.
    if (title && title !== baseTitle.trim()) {
      const repeated = repeatedTitle(parseDocument(content), baseTitle);
      if (repeated) content = replaceRange(content, repeated.start, repeated.end, retitledLine(repeated, title));
    }
    const base = baseVersion ?? editBase;
    updateArtifact.mutate(
      {
        artifactId: artifact.id,
        content,
        ...(title && title !== artifact.title ? { title } : {}),
        ...(draftNote.trim() ? { note: draftNote.trim() } : {}),
        ...(base !== null ? { baseVersion: base } : {}),
      },
      {
        onSuccess: () => {
          restoreEditFocusRef.current = true;
          setConflict(null);
          setEditing(false);
          resetDraft();
          forgetDraft(viewerId, artifactId);
        },
        onError: (error) => {
          const stale = readArtifactConflict(error);
          if (stale) {
            // Their text stays in the editor.
            setConflict(stale);
            return;
          }
          queue.add({
            title: "Couldn't save the document",
            description: getAiRequestErrorMessage(error),
            variant: "error",
          });
        },
      },
    );
  };

  /**
   * Start over from the version someone else saved. This person's unsaved
   * text is set aside, and Undo brings it back as it was.
   */
  const loadTheirVersion = async () => {
    const mine = snapshot();
    const latest = (await artifactQuery.refetch()).data;
    if (!latest) return;
    offerUndo(
      `Loaded v${latest.version}${conflict?.by ? `, ${conflict.by}’s` : ""}`,
      "Your unsaved text is set aside. Undo brings it back.",
      History,
      () => putBack(mine),
    );
    setDraftTitle(latest.title);
    setDraftContent(latest.content);
    setBaseTitle(latest.title);
    setBaseContent(latest.content);
    setOriginalContent(latest.content);
    seedEditor(latest.content, latest.title, true);
    setEditBase(latest.version);
    setConflict(null);
  };

  /** Bring an older version back as the newest one; every version stays in the history. */
  const restoreVersion = (revision: WorkshopArtifactRevision) => {
    if (!artifact || restoring !== null) return;
    setRestoring(revision.version);
    updateArtifact.mutate(
      {
        artifactId: artifact.id,
        content: revision.content,
        note: `Restored v${revision.version}`,
        baseVersion: artifact.version,
      },
      {
        onSuccess: (saved) => {
          setRestoring(null);
          setViewRevisionId(null);
          queue.add({
            title: `v${revision.version} is back, as v${saved.version}`,
            description: `v${artifact.version} stays in the history.`,
            variant: "success",
          });
        },
        onError: (error) => {
          setRestoring(null);
          const stale = readArtifactConflict(error);
          if (stale) void artifactQuery.refetch();
          queue.add({
            title: `Couldn't restore v${revision.version}`,
            description: stale
              ? `${stale.by ?? "Someone"} saved v${stale.currentVersion} just now. Nothing was lost; restore again to put v${revision.version} after it.`
              : getAiRequestErrorMessage(error),
            variant: "error",
          });
        },
      },
    );
  };

  const restoreStashed = () => {
    if (!artifact || !stashed) return;
    retireUndo();
    putBack({
      title: stashed.title,
      content: stashed.content,
      note: stashed.note,
      baseTitle: artifact.title,
      baseContent: artifact.content,
      originalContent: artifact.content,
      // Saving an edit that started from an older version asks, as any conflict.
      editBase: stashed.baseVersion,
      conflict: null,
    });
    setStashed(null);
  };

  const discardStashed = () => {
    const kept = stashed;
    if (!kept) return;
    forgetDraft(viewerId, artifactId);
    setStashed(null);
    offerUndo("Your unsaved edit was discarded", "Undo offers it back.", PencilOff, () => {
      stashDraft(viewerId, kept);
      setStashed(kept);
    });
  };

  const runExport = async (kind: "md" | "docx" | "pdf") => {
    if (!artifact) return;
    setMenuOpen(false);
    menuButtonRef.current?.focus();
    setExporting(true);
    try {
      if (kind === "md") {
        exportArtifactMarkdown(artifact.title, displayedContent, displayedVersion);
      } else if (kind === "pdf") {
        await exportArtifactPdf(artifact.title, displayedContent, displayedVersion);
      } else {
        await exportArtifactDocx(artifact.title, displayedContent, displayedVersion);
      }
      queue.add({
        title: `Exported v${displayedVersion}`,
        description: "The Workshop document stays editable.",
        variant: "success",
      });
    } catch (error) {
      queue.add({
        title: "Couldn't export the document",
        description: getAiRequestErrorMessage(error),
        variant: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  /** Arrow keys, Home and End walk the download menu; Tab leaves it. */
  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Tab") {
      setMenuOpen(false);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])'),
    );
    if (items.length === 0) return;
    event.preventDefault();
    const at = items.findIndex((item) => item === document.activeElement);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? items.length - 1
          : event.key === "ArrowDown"
            ? (at + 1 + items.length) % items.length
            : at < 0
              ? items.length - 1
              : (at - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  /** Outputs › the kind (a document's type once #114 lands, else "Document"). */
  const crumb = (current: string) => (
    <nav className="ws-pane-crumb" aria-label="Where this is">
      <button
        ref={breadcrumbRef}
        type="button"
        onClick={onBack}
        disabled={editing}
        title={editing ? "Finish or cancel editing first" : "Back to Outputs"}
        className="ws-pane-crumb-back"
      >
        Outputs
      </button>
      <ChevronRight className="size-3 shrink-0" strokeWidth={1.8} aria-hidden="true" />
      <span className="ws-pane-crumb-here" aria-current="page">
        {current}
      </span>
    </nav>
  );

  if (artifactQuery.isLoading) {
    return (
      <section
        className="ws-artifact-lens ws-pane-doc flex min-h-0 flex-1 flex-col"
        aria-labelledby={headingId}
        aria-busy="true"
      >
        <h2 id={headingId} className="sr-only">
          Loading the document
        </h2>
        <header className="ws-pane-head">
          {crumb("Document")}
          {actions}
        </header>
        <div className="ws-pane-doc-top" aria-hidden="true">
          <span className="ws-skel ws-skel--tag" />
          <span className="ws-skel ws-skel--title" />
          <span className="ws-skel ws-skel--line" style={{ width: "40%" }} />
          <span className="ws-skel ws-skel--line" style={{ width: "90%", marginTop: 24 }} />
          <span className="ws-skel ws-skel--line" style={{ width: "84%" }} />
          <span className="ws-skel ws-skel--line" style={{ width: "72%" }} />
        </div>
      </section>
    );
  }

  // Keep a cached artifact visible if a background refresh fails. The lens is a
  // working surface; a transient poll error must not replace the document.
  if (!artifact) {
    return (
      <section
        className="ws-artifact-lens ws-pane-doc flex min-h-0 flex-1 flex-col"
        aria-labelledby={headingId}
      >
        <header className="ws-pane-head">
          {crumb("Document")}
          {actions}
        </header>
        <div className="grid min-h-0 flex-1 place-items-center px-6 text-center">
          <div className="max-w-sm">
            <h2 id={headingId} className="text-base font-semibold text-co-text">
              Couldn't open this document
            </h2>
            <p className="mt-2 text-sm leading-6 text-co-muted">
              {getAiRequestErrorMessage(artifactQuery.error)}
            </p>
            <KitButton
              variant="secondary"
              iconLeft={RotateCcw}
              className="mt-4"
              onClick={() => void artifactQuery.refetch()}
            >
              Try again
            </KitButton>
          </div>
        </div>
      </section>
    );
  }

  const tag = documentTag(artifact.kind);
  const crumbHere = tag ? tag.charAt(0) + tag.slice(1).toLowerCase() : "Document";
  const shownDoc = editing ? editDoc : readDoc;
  const sections = shownDoc.sections.length;
  const characters = editing ? effectiveContent.length : displayedContent.length;
  const hidden = repeatedTitle(shownDoc, editing ? baseTitle : artifact.title);
  const editorial = !editing ? parseEditorialArtifact(displayedContent) : null;
  const asBlocks = readDoc.structured;
  const plainContent = hidden ? replaceRange(displayedContent, hidden.start, hidden.end, "") : displayedContent;
  const documentParts = splitFirstImage(plainContent);
  // Whoever wrote the version on screen: the viewed version's author, else the
  // latest editor, else the creator.
  const savedAttribution = viewedRevision
    ? viewedRevision.attribution?.createdBy
    : (artifact.attribution?.lastEditedBy ??
      currentRevision?.attribution?.createdBy ??
      artifact.attribution?.createdBy);
  const savedBy = savedByOf(savedAttribution, viewerId);
  const savedAt = viewedRevision?.createdAt ?? savedAttribution?.at ?? artifact.updatedAt;
  const nextVersion = (conflict ? conflict.currentVersion : currentVersion) + 1;
  const titleLength = draftTitle.trim().length;
  const saving = updateArtifact.isPending && restoring === null;

  const stateTag = viewedRevision
    ? `V${displayedVersion}`
    : artifact.status === "archived"
      ? `Archived · V${displayedVersion} · read-only`
      : `V${currentVersion} · current`;
  const meta = `${sections > 0 ? `${sections} ${sections === 1 ? "section" : "sections"} · ` : ""}${formatCount(characters)} characters`;
  const tagsRow = (
    <p className="ws-doc-tags">
      {tag ? <KindTag>{tag}</KindTag> : null}
      <StateTag>{stateTag}</StateTag>
      {isPhone ? null : <span className="ws-doc-meta">{meta}</span>}
    </p>
  );

  const versionPicker = (phone: boolean) =>
    olderRevisions.length > 0 ? (
      // The kit's dropdown: the chip says only the version, the list says who and when.
      <Select
        aria-label={`Version, showing v${displayedVersion}`}
        selectedKey={viewRevisionId ?? "current"}
        onSelectionChange={(key) => setViewRevisionId(key === "current" || key == null ? null : String(key))}
      >
        <Button className={phone ? "ws-doc-ctl ws-doc-version-ctl" : "ws-doc-version"}>
          {phone ? <History className="size-[15px]" strokeWidth={1.8} aria-hidden /> : null}
          <span>V{displayedVersion}</span>
          {phone ? null : <ChevronDown className="size-3 shrink-0" strokeWidth={1.8} aria-hidden />}
        </Button>
        <KitPopover placement="bottom end" className="w-[280px] max-w-[calc(100vw-24px)]">
          <ListBox className="max-h-[inherit] overflow-auto p-1.5 outline-none">
            <KitSelectItem id="current" textValue={`V${currentVersion}, current`}>
              <span className="truncate text-[12.5px]">V{currentVersion}, current</span>
            </KitSelectItem>
            {olderRevisions.map((revision) => (
              <KitSelectItem key={revision.id} id={revision.id} textValue={revisionLabel(revision, viewerId)}>
                <span className="truncate text-[12.5px]">{revisionLabel(revision, viewerId)}</span>
              </KitSelectItem>
            ))}
          </ListBox>
        </KitPopover>
      </Select>
    ) : phone ? (
      <span className="ws-doc-ctl ws-doc-ctl--static">
        <History className="size-[15px]" strokeWidth={1.8} aria-hidden />V{currentVersion}
      </span>
    ) : (
      <span className="ws-doc-version ws-doc-version--static">V{currentVersion}</span>
    );

  const unsavedLabel = conflict ? (
    <span className="ws-doc-unsaved" role="status">
      Someone saved v{conflict.currentVersion}
    </span>
  ) : dirty ? (
    <span className="ws-doc-unsaved" role="status">
      {isPhone ? "Unsaved" : "Unsaved changes"}
    </span>
  ) : null;

  const saveButton = (
    <button
      type="button"
      className="ws-doc-save"
      disabled={!dirty || !effectiveContent.trim() || saving}
      aria-busy={saving || undefined}
      onClick={() => saveEdit(conflict ? conflict.currentVersion : undefined)}
    >
      {saving ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
      Save v{nextVersion}
    </button>
  );

  const exportMenu = menuOpen ? (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Download version ${displayedVersion}`}
      onKeyDown={onMenuKeyDown}
      className={
        isPhone ? "ws-doc-sheet" : "co-dropdown-panel wb-glass-pop ws-menu ws-doc-menu"
      }
    >
      {isPhone ? (
        <>
          <span className="ws-sheet-grabber" aria-hidden="true" />
          <p className="ws-doc-sheet-title">Export</p>
          <p className="ws-doc-sheet-sub">
            {artifact.title} · V{displayedVersion}
          </p>
        </>
      ) : null}
      {EXPORT_FORMATS.map((format) => {
        const Icon = EXPORT_ICONS[format.id];
        const [, name, ext] = /^(.*?) \((\.\w+)\)$/.exec(format.label) ?? [format.label, format.label, ""];
        return (
          <button
            key={format.id}
            type="button"
            role="menuitem"
            className={isPhone ? "ws-doc-sheet-item" : "ws-menu-item ws-menu-button"}
            onClick={() => void runExport(format.id)}
          >
            <Icon className="size-[15px] shrink-0" strokeWidth={1.8} aria-hidden />
            {isPhone ? (
              <>
                <span>{name}</span>
                <span className="ws-doc-sheet-ext">{ext}</span>
                <Download className="ws-doc-sheet-go" strokeWidth={1.8} aria-hidden />
              </>
            ) : (
              format.label
            )}
          </button>
        );
      })}
    </div>
  ) : null;

  const notesButton = (phone: boolean) => (
    <button
      type="button"
      className={phone ? "ws-doc-ctl" : "ws-head-icon ws-doc-notes"}
      aria-pressed={notesOpen}
      aria-label={`Notes, ${openNoteCount} open`}
      title={canEdit ? "Notes. Select text to add one" : "Notes"}
      onClick={() => (notesOpen ? closeNotes() : setNotesOpen(true))}
    >
      <MessageSquareText className="size-[15px]" strokeWidth={1.8} aria-hidden />
      {phone ? <span aria-hidden="true">Notes {openNoteCount}</span> : (
        <span className="ws-room-count">{openNoteCount}</span>
      )}
    </button>
  );

  const downloadButton = (phone: boolean) => (
    <button
      ref={menuButtonRef}
      type="button"
      className={phone ? "ws-doc-ctl" : "ws-head-icon"}
      aria-label={`Download version ${displayedVersion}`}
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      aria-disabled={exporting || undefined}
      title="Download a copy; the document stays editable"
      onClick={() => {
        if (exporting) return;
        setMenuOpen((current) => !current);
      }}
    >
      {exporting ? (
        <Loader2 className="size-[15px] animate-spin" aria-hidden />
      ) : (
        <Download className="size-[15px]" strokeWidth={1.8} aria-hidden />
      )}
      {phone ? <span aria-hidden="true">Export</span> : null}
    </button>
  );

  const editButton = (phone: boolean) =>
    canEdit ? (
      <button
        ref={editButtonRef}
        type="button"
        className={phone ? "ws-doc-ctl ws-doc-ctl--primary" : "ws-doc-edit"}
        onClick={startEditing}
      >
        <Pencil className="size-[13px]" strokeWidth={2} aria-hidden />
        Edit
      </button>
    ) : null;

  const head = editing ? (
    isPhone ? (
      <header className="ws-pane-head ws-doc-edit-head">
        <button type="button" className="ws-doc-cancel" onClick={cancelEditing}>
          Cancel
        </button>
        <div className="ws-pane-actions">
          {unsavedLabel}
          {saveButton}
        </div>
      </header>
    ) : (
      // Output · editing (07 Workshop, 9:41318): the accent Editing pill ends
      // the edit (Undo brings unsaved words back); Save is in the foot.
      <header className="ws-pane-head">
        {crumb(crumbHere)}
        <div className="ws-pane-actions ws-doc-actions">
          {unsavedLabel}
          <button
            type="button"
            className="ws-doc-edit ws-doc-editing"
            aria-pressed
            title="Stop editing"
            onClick={cancelEditing}
          >
            <Pencil className="size-[13px]" strokeWidth={2} aria-hidden />
            Editing
          </button>
          {actions}
        </div>
      </header>
    )
  ) : (
    <header className="ws-pane-head">
      {crumb(crumbHere)}
      <div ref={actionsRef} className="ws-pane-actions ws-doc-actions">
        {isPhone ? null : (
          <>
            {notesButton(false)}
            {versionPicker(false)}
            {editButton(false)}
            {downloadButton(false)}
            {exportMenu}
          </>
        )}
        {actions}
      </div>
    </header>
  );

  const stashStrip =
    stashed &&
    canEdit &&
    !editing &&
    (lf(stashed.content) !== lf(artifact.content) || stashed.title !== artifact.title) ? (
      <StashStrip
        when={formatDayTime(stashed.savedAt)}
        onRestore={restoreStashed}
        onDiscard={discardStashed}
      />
    ) : null;

  const olderStrip =
    viewedRevision ? (
      <OlderVersionStrip
        version={viewedRevision.version}
        currentVersion={currentVersion}
        when={formatDayTime(viewedRevision.createdAt, "at")}
        by={savedByOf(viewedRevision.attribution?.createdBy, viewerId)}
        canRestore={canWrite && artifact.status !== "archived"}
        restoring={restoring === viewedRevision.version}
        onRestore={() => restoreVersion(viewedRevision)}
        onBack={() => setViewRevisionId(null)}
      />
    ) : null;

  const reader = (
    <>
      <div className="ws-doc-top">
        {tagsRow}
        {isPhone ? null : (stashStrip ?? olderStrip)}
        <h2 id={headingId} className="ws-pane-doc-title">
          {artifact.title}
        </h2>
        {isPhone ? (
          <div className="ws-doc-controls">
            {notesButton(true)}
            {versionPicker(true)}
            {downloadButton(true)}
            {editButton(true)}
          </div>
        ) : (
          <SavedLine when={formatDayTime(savedAt)} by={savedBy} onOpenThread={onOpenThread} />
        )}
        {isPhone ? (stashStrip ?? olderStrip) : null}
      </div>
      <div ref={docRef}>
        {editorial ? (
          <EditorialDocument layout={editorial} hideTitle={!!hidden} />
        ) : asBlocks ? (
          <ArtifactDocument
            doc={readDoc}
            hiddenKey={hidden?.key ?? null}
            showIndex={!isPhone}
            scrollRef={scrollRef}
          />
        ) : (
          <article className="ws-artifact-lens-document">
            {documentParts.before ? (
              <ChatMarkdown className="streamdown-doc" content={documentParts.before} />
            ) : null}
            {documentParts.image ? (
              <figure className="ws-artifact-media">
                <img src={documentParts.image.src} alt={documentParts.image.alt} loading="eager" />
              </figure>
            ) : null}
            {documentParts.after ? (
              <ChatMarkdown className="streamdown-doc" content={documentParts.after} />
            ) : null}
          </article>
        )}
      </div>
    </>
  );

  const editor = (
    <>
      {conflict ? (
        <ArtifactConflictNotice
          conflict={conflict}
          busy={updateArtifact.isPending || artifactQuery.isFetching}
          onLoadTheirs={() => void loadTheirVersion()}
          onKeepMine={() => saveEdit(conflict.currentVersion)}
        />
      ) : null}
      <div className="ws-doc-top">
        {tagsRow}
        <h2 id={headingId} className="sr-only">
          Editing {artifact.title}
        </h2>
        <div className="ws-doc-titlefield">
          <label htmlFor={titleId} className="sr-only">
            Title
          </label>
          <TitleInput
            id={titleId}
            describedBy={counterId}
            value={draftTitle}
            onChange={setDraftTitle}
          />
          <p id={counterId} className="ws-doc-title-rule">
            <span className="ws-doc-title-count" data-over={titleLength > TITLE_LIMIT || undefined}>
              {titleLength} / {TITLE_LIMIT}
            </span>
            <span>Titles read “Type, subject”. Two lines at most.</span>
          </p>
        </div>
      </div>
      {facts && facts.length > 0 ? (
        <dl className="ws-doc-facts ws-rich-facts">
          {facts.map((fact, index) => {
            const name = fact.key.replace(/[_-]+/g, " ").trim();
            return (
              <div key={`${fact.key}-${index}`} className="ws-doc-fact">
                <dt>{name}</dt>
                <dd>
                  <textarea
                    rows={1}
                    value={fact.value}
                    onChange={(event) => onFactChange(index, event.target.value.replace(/\n/g, " "))}
                    aria-label={name}
                    className="ws-rich-fact"
                  />
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}
      <div ref={docRef}>
        <RichDocEditor
          key={seed.key}
          markdown={seed.body}
          onChange={onEditorChange}
          onRewrite={rewrite}
          isPhone={isPhone}
          label={`${draftTitle.trim() || artifact.title}, the document`}
        />
      </div>
    </>
  );

  return (
    <section
      className="ws-artifact-lens ws-pane-doc flex min-h-0 flex-1 flex-col overflow-hidden"
      aria-labelledby={headingId}
      data-phone={isPhone || undefined}
    >
      {head}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          className="ws-doc-scroll custom-scrollbar"
          data-editing={editing || undefined}
        >
          {editing ? editor : reader}
          {!editing && noteLayer.prompt ? (
            <AddNoteButton prompt={noteLayer.prompt} onAdd={startNote} />
          ) : null}
        </div>

        {editing ? (
          <form
            className="ws-doc-foot"
            onSubmit={(event) => {
              event.preventDefault();
              saveEdit(conflict ? conflict.currentVersion : undefined);
            }}
          >
            <label htmlFor={noteId} className="sr-only">
              What changed
            </label>
            <input
              id={noteId}
              value={draftNote}
              onChange={(event) => setDraftNote(event.target.value)}
              placeholder="What changed? (optional, shown in the history)"
              maxLength={200}
              className="ws-doc-note"
            />
            {saveButton}
          </form>
        ) : null}

        {notesOpen && !editing ? (
          <ArtifactNotesDrawer
            variant={isPhone ? "sheet" : "popover"}
            notes={notes}
            loading={notesQuery.isLoading}
            ranges={noteLayer.ranges}
            locatable={notesLive}
            activeNoteId={activeNoteId}
            viewerId={viewerId}
            canWrite={canWrite}
            isProjectOwner={isProjectOwner}
            draft={draftAnchor}
            onCancelDraft={() => setDraftAnchor(null)}
            onSubmitDraft={submitNote}
            onReveal={(noteId) => {
              setActiveNoteId(noteId);
              noteLayer.revealNote(noteId);
            }}
            onSave={(noteId, body) => updateNote.mutate({ noteId, body })}
            onResolve={(noteId) => updateNote.mutate({ noteId, status: "resolved" })}
            onReopen={(noteId) => updateNote.mutate({ noteId, status: "open" })}
            onDelete={(noteId) => {
              if (activeNoteId === noteId) setActiveNoteId(null);
              deleteNote.mutate(noteId);
            }}
            onClose={closeNotes}
          />
        ) : null}

        {isPhone && menuOpen ? (
          <>
            <button
              type="button"
              className="ws-doc-scrim"
              aria-label="Close export"
              tabIndex={-1}
              onClick={() => setMenuOpen(false)}
            />
            {exportMenu}
          </>
        ) : null}
      </div>
    </section>
  );
}

/** The image-led editorial layout, kept as it was; its H1 is left out when it repeats the title. */
function EditorialDocument({
  layout,
  hideTitle,
}: {
  layout: EditorialArtifact;
  hideTitle: boolean;
}) {
  return (
    <article className="ws-artifact-editorial">
      <header className="ws-artifact-editorial-hero">
        <div className="ws-artifact-editorial-hero-copy">
          {hideTitle ? null : (
            <h1 className="ws-artifact-editorial-title">{scrubVendorTerms(layout.title)}</h1>
          )}
          {layout.lead ? (
            <ChatMarkdown
              className="ws-artifact-editorial-lead"
              content={layout.lead}
            />
          ) : null}
        </div>

        <figure className="ws-artifact-editorial-media">
          <img
            src={layout.image.src}
            alt={layout.image.alt}
            loading="eager"
          />
          {layout.caption ? (
            <figcaption className="ws-artifact-editorial-caption">
              <ChatMarkdown content={layout.caption} />
            </figcaption>
          ) : null}
        </figure>
      </header>

      <div className="ws-artifact-editorial-columns">
        {layout.columns.map((section, index) => (
          <section
            key={`${section.heading}-${index}`}
            className="ws-artifact-editorial-section"
          >
            <span
              className="ws-artifact-editorial-number"
              aria-hidden="true"
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <h2 className="ws-artifact-editorial-section-title">
              {scrubVendorTerms(section.heading)}
            </h2>
            <ChatMarkdown
              className="ws-artifact-editorial-section-copy"
              content={section.content}
            />
          </section>
        ))}
      </div>

      {layout.supplementalSections.length > 0 ? (
        <div className="ws-artifact-editorial-supplemental">
          {layout.supplementalSections.map((section, index) => (
            <section
              key={`${section.heading}-${index}`}
              className="ws-artifact-editorial-section ws-artifact-editorial-section--supplemental"
            >
              <h2 className="ws-artifact-editorial-section-title">
                {scrubVendorTerms(section.heading)}
              </h2>
              <ChatMarkdown
                className="ws-artifact-editorial-section-copy"
                content={section.content}
              />
            </section>
          ))}
        </div>
      ) : null}

      {layout.opportunity ? (
        <footer className="ws-artifact-editorial-opportunity">
          <h2 className="ws-artifact-editorial-opportunity-title">
            {scrubVendorTerms(layout.opportunity.heading)}
          </h2>
          <ChatMarkdown
            className="ws-artifact-editorial-opportunity-copy"
            content={layout.opportunity.content}
          />
        </footer>
      ) : null}
    </article>
  );
}

/** The title, in the reading title's own type: up to two lines, no line breaks of its own. */
function TitleInput({
  id,
  describedBy,
  value,
  onChange,
}: {
  id: string;
  describedBy: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    box.style.height = "auto";
    box.style.height = `${box.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      id={id}
      rows={1}
      value={value}
      maxLength={200}
      aria-describedby={describedBy}
      className="ws-doc-title-input"
      onChange={(event) => onChange(event.target.value.replace(/\s*\n\s*/g, " "))}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.preventDefault();
      }}
    />
  );
}
